import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';

export default function FAQ() {
  const faqs = [
    {
      question: '1. How do I sign up and get access to the app?',
      answer: (
        <>
          <Typography component="div">
            <ul>
              <li>Click <strong>Sign Up</strong> in the top-right corner.</li>
              <li>Provide your email, password, and display name.</li>
              <li>
                You’ll then need to authenticate your account (automatic with Google, email link otherwise) and
                ensure you have active subscription time.
              </li>
            </ul>
          </Typography>
        </>
      )
    },
    {
      question: '2. What do the different menu items mean?',
      answer: (
        <Typography component="div">
          <ul>
            <li><strong>Dashboard:</strong> at-a-glance summary of harvests, orders, and upcoming tasks.</li>
            <li><strong>Harvests:</strong> plan or log plantings/harvests.</li>
            <li><strong>Orders:</strong> create customer orders and generate invoices.</li>
            <li><strong>Tasks:</strong> view reminders created by harvests/orders (table/calendar), or make custom tasks.</li>
            <li><strong>Management:</strong> customer database, inventory, and farm settings.
              <ul>
                <li><strong>Seeds:</strong> add and edit crop varieties you grow.</li>
                <li><strong>Trays:</strong> manage your growing trays’ details and status.</li>
                <li><strong>Farm:</strong> members/roles, invite code, farm settings, and backup/danger actions.</li>
              </ul>
            </li>
            <li><strong>User Menu:</strong> account, help, logout, mode toggle.
              <ul>
                <li><strong>Account:</strong> set your display name, manage timecodes/subscription details, and account-level danger actions.
                </li>
                <li><strong>Help:</strong> contains this FAQ, getting started guide, contact info, and privacy policy.</li>
                <li><strong>Logout:</strong> logs you out.</li>
                <li><strong>Mode Toggle:</strong> toggle between Light and Dark mode; defaults to system, saved locally.</li>
              </ul>
            </li>
          </ul>
        </Typography>
      )
    },
    {
      question: '3. How do I add a new crop variety (seed)?',
      answer: (
<Typography component="div">
  <ul>
    <li>
      Navigate to <strong>Management → Seeds → Add Crop</strong>.
    </li>
    <li>
      Fill in the name, soak time, growth cycle, etc.
    </li>
    <li>
      Click <strong>Save</strong>. Use the <SettingsIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }}/>menu to edit or delete later.
    </li>
  </ul>
</Typography>

      )
    },
    {
      question: '4. Can I bulk-create harvests/orders/seeds/etc? ',
      answer: (
        <Typography component="div">
          <ul>
            <li>Use <strong>Harvest Templates</strong> inside the Add Harvest form to define common plantings.</li>
            <li>From a template, you can quickly spin up multiple plantings.</li>
            <li>All harvest, order, and inventory entries can be cloned via the context menu.</li>
          </ul>
        </Typography>
      )
    },
    {
      question: '5. What happens when I complete an order?',
      answer: (
        <Typography component="div">
          <ul>
            <li>The app runs a Cloud Function that:</li>
            <ol>
              <li>Generates a PDF invoice in Storage.</li>
              <li>Records it under Invoices.</li>
              <li>Removes the active order from the database.</li>
            </ol>
            <li>You can download the invoice via the link in Orders.</li>
          </ul>
        </Typography>
      )
    },
    {
      question: '6. How do tasks work?',
      answer: (
        <Typography component="div">
          <ul>
            <li>System-generated tasks include seed soaking, planting, harvesting, and delivery reminders based on your dates.</li>
            <li>You can add custom tasks: click <strong>Add Task</strong>, set name, date, description, and emoji/color.</li>
            <li>Mark tasks <strong>Complete</strong> to remove them from your active list.</li>
          </ul>
        </Typography>
      )
    },
    {
      question: '7. How do I switch between light and dark mode?',
      answer: (
        <Typography component="div">
          <ul>
            <li>Use the toggle in the user menu drawer to switch instantly.</li>
            <li>Defaults to system setting on first use, saved locally afterwards.</li>
          </ul>
        </Typography>
      )
    },
    {
      question: '8. Can I back up or export my data?',
      answer: (
        <Typography component="div">
          <ul>
            <li>Yes. In <strong>Management → Farm</strong>, owners can export/import farm data via ZIP backup tools.</li>
            <li>Backup/import controls are owner-only.</li>
          </ul>
        </Typography>
      )
    },
    {
      question: '9. Can I delete my farm data or my account?',
      answer: (
        <Typography component="div">
          <ul>
            <li>Yes. Account deletion is available in <strong>Account</strong>.</li>
            <li>Farm-level danger actions are available in <strong>Management → Farm</strong> for owners.</li>
            <li><strong>Warning:</strong> these actions are irreversible.</li>
          </ul>
        </Typography>
      )
    },
    {
      question: '10. Can I allow access to my employees to manage my farm data?',
      answer: (
        <Typography component="div">
          <ul>
            <li>Yes. Multi-user access is live with role-based permissions.</li>
            <li>Use your farm invite code to add members, then assign roles (owner/admin/worker/member).</li>
            <li>Permissions vary by role; owner-only actions include backup/import and farm danger actions.</li>
          </ul>
        </Typography>
      )
    },
    {
      question: '11. Can I set up recurring orders/harvests?',
      answer: (
        <Typography component="div">
          <ul>
            <li>Recurring orders are supported in the order form.</li>
            <li>Harvest recurrence is not currently a single-click recurring feature; use templates to speed repeat entries.</li>
          </ul>
        </Typography>
      )
    },
    {
      question: '12. Will I lose my settings if I switch devices?',
      answer: (
        <Typography component="div">
          <ul>
            <li>No: almost everything is synced via Firestore (harvests, orders, tasks, settings).</li>
            <li>Your light/dark mode preference is stored locally and defaults to system on new devices.</li>
          </ul>
        </Typography>
      )
    },
    {
      question: '13. What browsers and devices are supported?',
      answer: (
        <Typography component="div">
          <ul>
            <li>Recommended: latest Chrome, Firefox, Safari, or Edge.</li>
            <li>Fully responsive on desktop, tablet, and mobile.</li>
          </ul>
        </Typography>
      )
    },
    {
      question: '14. How do farm switching and active farm selection work?',
      answer: (
        <Typography component="div">
          <ul>
            <li>If you belong to multiple farms, open the account drawer and use the farm selector tile/dropdown.</li>
            <li>All pages show data for your currently active farm.</li>
            <li>If your membership is removed from the active farm, the app will move you to another available farm when possible.</li>
          </ul>
        </Typography>
      )
    },
    {
      question: '15. Are order blends supported?',
      answer: (
        <Typography component="div">
          <ul>
            <li>Yes. Orders can include blends with multiple harvest lines in one product entry.</li>
            <li>Each blend line can use its own harvest and portions.</li>
          </ul>
        </Typography>
      )
    }
  ];
  return (
    <Box
      sx={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '20px',
        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.primary,
      }}
    >
      <Typography variant="h4" gutterBottom>
        Frequently Asked Questions
      </Typography>
      <Typography variant="body2" gutterBottom sx={{ fontStyle: 'italic' }}>
        Last Updated: 13/02/2026
      </Typography>
          <Divider sx={{ width: '100%', my: 2 }} />
      {faqs.map((faq, idx) => (
        <Accordion key={idx} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">{faq.question}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {faq.answer}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
